import React, { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { v4 as uuid } from 'uuid'
import Papa from 'papaparse'
import { IconFileExport, IconFileTypePdf } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'

import { editScorecardConfig, setError } from '@/reducers/qa-copilot/qa-copilot.redux'
import { addPrompt } from '@/reducers/prompts/prompts.redux'
import { explainCopilotPrompt, generateCopilotPrompt } from '@/reducers/prompts/prompts.actions'
import { Button } from '@/components/buttons/Button'

import { getDefaultQaCopilotModelId } from '../QACopilot.helpers'
import { buildScorecardPdfDocument, getScorecardPdfFileName } from './scorecardPdf.helpers'

// Regex needed to ensure transcript placeholder exists in generated prompts
const TRANSCRIPT_BLOCK_REGEX = /#START OF TRANSCRIPT[\s\S]*?#END OF TRANSCRIPT/g
const EXPORT_CSV_COLUMNS = [
  'Section Name',
  'Criterion Name',
  'Criterion Description',
  'Criterion Prompt',
  'Type',
]
const GENERATE_REQUIRED_COLUMNS = [
  'Criterion Description',
  'Scenarios of when this criterion should be passed',
]

const getCriteriaTypeFromCsv = (type) => {
  if (String(type || '').trim() === '0') {
    return 'manual'
  }

  return 'ai'
}

const getCsvValue = (row, headerName) => {
  const directValue = row[headerName]

  if (directValue != null) {
    return directValue
  }

  const normalizedHeaderName = headerName.toLowerCase()
  const matchingHeader = Object.keys(row).find(
    (header) => header.toLowerCase() === normalizedHeaderName
  )

  return matchingHeader != null ? row[matchingHeader] : undefined
}

export const normalizeCsvRow = (row) => ({
  criterionName: String(getCsvValue(row, 'Criterion Name') || '').trim(),
  sectionName: String(getCsvValue(row, 'Section Name') || '').trim(),
  description: String(getCsvValue(row, 'Criterion Description') || '').trim(),
  prompt: String(getCsvValue(row, 'Criterion Prompt') || getCsvValue(row, 'Prompt') || '').trim(),
  context: String(
    getCsvValue(row, 'Scenarios of when this criterion should be passed') || ''
  ).trim(),
  criteriaType: getCriteriaTypeFromCsv(getCsvValue(row, 'Type')),
})

export const getScorecardExportRows = ({ sections = [], prompts = [] }) => {
  const promptsById = new Map()
  const promptsByScid = new Map()

  prompts.forEach((prompt) => {
    if (prompt.id != null) {
      promptsById.set(prompt.id, prompt)
    }

    if (prompt.scid != null) {
      promptsByScid.set(prompt.scid, prompt)
    }
  })

  return sections.flatMap((section) => {
    const criteriaList = section.measures?.[0]?.criteria || []

    return criteriaList.map((criteria) => {
      const prompt = promptsById.get(criteria.prompt_id) || promptsByScid.get(criteria.scid) || null

      return {
        'Section Name': section.name || '',
        'Criterion Name': criteria.name || '',
        'Criterion Description': criteria.description || '',
        'Criterion Prompt': prompt?.user_prompt || '',
        promptId: criteria.prompt_id ?? null,
        Type: criteria.criteria_type === 'manual' ? '0' : '1',
      }
    })
  })
}

export const getScorecardExportFileName = (scorecardName) =>
  `${scorecardName || 'scorecard'}-autofill-export.csv`

export { buildScorecardPdfDocument, getScorecardPdfFileName }

export const canGeneratePromptsFromCsv = ({ csvData = [], csvHeaders = [] }) => {
  const normalizedHeaders = csvHeaders.map((header) => header.toLowerCase())
  const hasRequiredColumns = GENERATE_REQUIRED_COLUMNS.every((columnName) =>
    normalizedHeaders.includes(columnName.toLowerCase())
  )

  return (
    hasRequiredColumns &&
    !isEmpty(csvData) &&
    csvData.every((row) => !isEmpty(row.description) && !isEmpty(row.context))
  )
}

export const isScorecardEmpty = (scorecardConfig) =>
  isEmpty(scorecardConfig) || isEmpty(scorecardConfig.sections)

export const getExportDisabledTooltip = ({ scorecardConfig, isOutdatedScorecard }) => {
  if (isScorecardEmpty(scorecardConfig)) {
    return 'No scorecard available to export'
  }

  if (isOutdatedScorecard) {
    return 'Export is only available on the latest version'
  }

  return null
}

export const QACopilotAutoPopulate = ({ isOutdatedScorecard }) => {
  const dispatch = useDispatch()

  const [csvData, setCsvData] = useState([])
  const [csvHeaders, setCsvHeaders] = useState([])
  const [fileName, setFileName] = useState('')
  const [fileUploaded, setFileUploaded] = useState(false)
  const [processingMode, setProcessingMode] = useState(null) // 'add' | 'generate' | null
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const { modelOptions, prompts } = useSelector((state) => state.prompts)

  const { scorecardConfig } = useSelector((state) => state.qaCopilot)

  const { organizationid, prompt_editor } = useSelector((state) => state.currentUser)

  const showAutofillSection = organizationid === 1 || prompt_editor

  const ensureTranscriptPlaceholder = useCallback((prompt = '') => {
    const withoutTranscript = prompt.replace(TRANSCRIPT_BLOCK_REGEX, '').trim()
    return `#START OF TRANSCRIPT\n$transcript\n#END OF TRANSCRIPT\n\n${withoutTranscript}`
  }, [])

  const qaCopilotModelOptions = modelOptions?.qa_copilot || []
  const defaultQaCopilotModelId = useMemo(
    () => getDefaultQaCopilotModelId(qaCopilotModelOptions),
    [qaCopilotModelOptions]
  )
  const exportRows = useMemo(
    () =>
      getScorecardExportRows({
        sections: scorecardConfig?.sections || [],
        prompts,
      }),
    [prompts, scorecardConfig?.sections]
  )

  const generatePrompt = useCallback(
    async ({ description, context }) => {
      const res = await dispatch(generateCopilotPrompt('qa', description, context))
      return ensureTranscriptPlaceholder(res?.generated_prompt || '')
    },
    [dispatch, ensureTranscriptPlaceholder]
  )

  const createCriterionWithPrompt = useCallback(
    (row, sectionSsid) => {
      const scid = uuid()
      const isManualCriteria = row.criteriaType === 'manual'
      const hasPrompt = !isEmpty(row.prompt)

      if (!isManualCriteria || hasPrompt) {
        dispatch(
          addPrompt({
            scid,
            user_prompt: row.prompt,
            side: 'both',
            llm_model_id: defaultQaCopilotModelId,
            feature: 'qa_copilot',
          })
        )
      }

      return {
        scid,
        name: row.criterionName,
        description: row.description,
        criteria_type: row.criteriaType,
        must_be_present: !isManualCriteria,
        manual_timestamp_markers: isManualCriteria && hasPrompt,
        side: 'both',
        max_response_tokens: 256,
        weight: 1,
        ssid: sectionSsid,
      }
    },
    [dispatch, defaultQaCopilotModelId]
  )

  const onDrop = useCallback(
    ([file]) => {
      if (!file) return

      setFileName(file.name)
      setCsvData([])
      setCsvHeaders([])
      setFileUploaded(false)

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, errors, meta }) => {
          if (errors?.length) {
            dispatch(setError('Error parsing CSV'))
            return
          }

          const normalizedRows = data.map(normalizeCsvRow).filter((row) => row.criterionName !== '')

          setCsvHeaders(meta?.fields || [])
          setCsvData(normalizedRows)
          setFileUploaded(true)
        },
        error: (err) => dispatch(setError(err.message)),
      })
    },
    [dispatch]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    multiple: false,
  })

  const handleExportCSV = () => {
    const csvText = Papa.unparse({
      fields: EXPORT_CSV_COLUMNS,
      data: exportRows.map((exportRow) =>
        EXPORT_CSV_COLUMNS.map((columnName) => exportRow[columnName] || '')
      ),
    })

    const csvBlob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
    const exportUrl = URL.createObjectURL(csvBlob)
    const link = document.createElement('a')

    link.href = exportUrl
    link.setAttribute('download', getScorecardExportFileName(scorecardConfig?.name))
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(exportUrl)
  }

  const handleExportPDF = async () => {
    setIsExportingPdf(true)
    try {
      const pdfRows = await Promise.all(
        exportRows.map(async (row) => {
          const promptText = row['Criterion Prompt']
          const description = row['Criterion Description']
          // Manual criteria aren't AI-scored, so there's nothing to explain.
          if (row.Type === '0' || (isEmpty(promptText) && isNil(row.promptId))) {
            return { ...row, guidelines: description }
          }
          const result = await dispatch(
            explainCopilotPrompt(
              isEmpty(promptText) ? { promptId: row.promptId } : { prompt: promptText }
            )
          )
          return { ...row, guidelines: result || description }
        })
      )
      const doc = buildScorecardPdfDocument({
        scorecardName: scorecardConfig?.name,
        exportRows: pdfRows,
      })
      doc.save(getScorecardPdfFileName(scorecardConfig?.name))
    } catch (err) {
      dispatch(setError('Failed to export scorecard as PDF'))
    } finally {
      setIsExportingPdf(false)
    }
  }

  const handleProcessCSV = useCallback(
    async ({ shouldGenerate }) => {
      setProcessingMode(shouldGenerate ? 'generate' : 'add')

      try {
        const sectionsMap = new Map()
        let lastSectionName = null

        for (const row of csvData) {
          const sectionName =
            row.sectionName || lastSectionName || `Section ${sectionsMap.size + 1}`

          lastSectionName = sectionName

          const prompt = shouldGenerate
            ? await generatePrompt({
                description: row.description,
                context: row.context,
              })
            : row.prompt

          if (!sectionsMap.has(sectionName)) {
            sectionsMap.set(sectionName, [])
          }

          sectionsMap.get(sectionName).push({
            criterionName: row.criterionName,
            description: row.description,
            prompt,
            criteriaType: row.criteriaType,
          })
        }

        const updatedConfig = {
          ...scorecardConfig,
          sections: scorecardConfig.sections.filter(
            (section) => section.name !== 'Default Section'
          ),
        }

        let orderId = updatedConfig.sections.length

        sectionsMap.forEach((criteria, sectionName) => {
          const ssid = uuid()

          updatedConfig.sections.push({
            ssid,
            name: sectionName,
            order_id: orderId++,
            measures: [
              {
                name: `${sectionName} Measure`,
                criteria: criteria.map((row) => createCriterionWithPrompt(row, ssid)),
              },
            ],
          })
        })

        dispatch(editScorecardConfig(updatedConfig))
      } finally {
        setProcessingMode(null)
        setCsvData([])
        setCsvHeaders([])
        setFileUploaded(false)
        setFileName('')
      }
    },
    [csvData, scorecardConfig, dispatch, generatePrompt, createCriterionWithPrompt]
  )
  const canGeneratePrompts = canGeneratePromptsFromCsv({ csvData, csvHeaders })

  // Imported criteria get their prompt stamped with `defaultQaCopilotModelId` here
  // rather than in the criteria form, so this is the only place that can require a
  // model for them. With the catalog empty there is nothing to give them, and a
  // prompt with no model has nothing for the API to run on — so stop at the import
  // rather than at the save. Only where a prompt is actually minted: an "Add" of
  // manual criteria with no prompt text writes none (see createCriterionWithPrompt),
  // while "Generate" fills a prompt in for every row and so always writes one.
  const noModelAvailable = !defaultQaCopilotModelId
  const addWouldCreatePrompts = csvData.some(
    (row) => row.criteriaType !== 'manual' || !isEmpty(row.prompt)
  )
  const noModelTooltip = 'AI models are still loading — wait a moment and try again'
  const exportDisabledTooltip = getExportDisabledTooltip({ scorecardConfig, isOutdatedScorecard })

  const exportPdfButton = (
    <Button
      basic
      data-testid="qa-copilot-export-scorecard-pdf-button"
      onClick={handleExportPDF}
      disabled={isScorecardEmpty(scorecardConfig) || isOutdatedScorecard || isExportingPdf}
      disabledTooltip={exportDisabledTooltip}
    >
      <IconFileTypePdf />
      {isExportingPdf ? 'Generating...' : 'Export Explanation'}
    </Button>
  )

  // PDF export is available to all QA Copilot users; the autofill tools below stay
  // gated behind prompt-editing access.
  if (!showAutofillSection) {
    return <div className="flex-align-center flex-end">{exportPdfButton}</div>
  }

  return (
    <div className="qa-copilot-autopopulate" data-testid="qa-copilot-autofill">
      <div className="flex-align-center flex-space-between flex-wrap gap mb">
        <h2 className="no-margin">Autofill Scorecard</h2>
        <div className="flex-align-center gap">
          {exportPdfButton}
          <Button
            basic
            data-testid="qa-copilot-export-scorecard-button"
            onClick={handleExportCSV}
            disabled={isScorecardEmpty(scorecardConfig) || isOutdatedScorecard}
            disabledTooltip={exportDisabledTooltip}
          >
            <IconFileExport />
            Export Scorecard
          </Button>
        </div>
      </div>

      <div
        {...(!isOutdatedScorecard ? getRootProps() : {})}
        className={`qa-copilot-autopopulate__dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} type="file" />
        <p>
          {isOutdatedScorecard
            ? 'File selection disabled in read-only mode'
            : 'Drag & drop a CSV file here, or click to select file'}
        </p>
      </div>

      {fileName && (
        <p className="qa-copilot-autopopulate__selected-file">
          Selected file: <strong>{fileName}</strong>
        </p>
      )}

      {fileUploaded && (
        <div className="flex-align-center medium-gap">
          <Button
            brand
            onClick={() => handleProcessCSV({ shouldGenerate: false })}
            disabled={
              processingMode !== null ||
              !csvData.length ||
              (noModelAvailable && addWouldCreatePrompts)
            }
            disabledTooltip={noModelAvailable ? noModelTooltip : undefined}
          >
            {processingMode === 'add' ? 'Processing...' : `Add ${csvData.length} Criteria`}
          </Button>

          <Button
            brand
            onClick={() => handleProcessCSV({ shouldGenerate: true })}
            disabled={processingMode !== null || !canGeneratePrompts || noModelAvailable}
            disabledTooltip={
              noModelAvailable
                ? noModelTooltip
                : 'CSV must include Criterion Description and Scenarios of when this criterion should be passed for every row to generate prompts'
            }
          >
            {processingMode === 'generate'
              ? 'Generating...'
              : `Generate ${csvData.length} Criteria`}
          </Button>
        </div>
      )}
    </div>
  )
}
